import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const rawBaseQuery = fetchBaseQuery({
  baseUrl: "/api",
  prepareHeaders: (headers) => {
    const token = localStorage.getItem("token");
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

/** Nginx 502 vb. HTML döndüğünde RTK varsayılanı SyntaxError verir; kullanıcıya anlamlı mesaj. */
function baseQuery(args, api, extraOptions) {
  return rawBaseQuery(args, api, extraOptions).then((result) => {
    if (!result.error) return result;
    const er = result.error;
    const rawData = er.data;
    const errText = typeof er.error === "string" ? er.error : "";
    const htmlBody = typeof rawData === "string" && rawData.trimStart().startsWith("<");
    const parseFail =
      er.status === "PARSING_ERROR" ||
      errText.includes("Unexpected token") ||
      errText.includes("is not valid JSON") ||
      htmlBody;

    if (!parseFail) return result;

    const st = er.originalStatus;
    const is502 = st === 502 || st === 503 || st === 504;
    return {
      error: {
        status: typeof st === "number" ? st : 502,
        data: {
          message: is502
            ? "Sistem geçici olarak yanıt vermiyor. Birkaç dakika sonra tekrar deneyin."
            : "İşlem tamamlanamadı. Sayfayı yenileyin veya tekrar giriş yapın.",
        },
      },
    };
  });
}

export const api = createApi({
  baseQuery,
  reducerPath: "adminApi",
  tagTypes: [
    "User",
    "Products",
    "Customers",
    "Transactions",
    "Geography",
    "Sales",
    "Admins",
    "Performance",
    "Dashboard",
    "Orders",
    "AbandonedCarts",
    "PopularProducts",
    "BlogPosts",
  ],
  endpoints: (build) => ({
    getUser: build.query({
      query: (id) => `general/user/${id}`,
      providesTags: ["User"],
    }),
    getProducts: build.query({
      query: () => "client/products",
      providesTags: ["Products"],
    }),
    getCustomers: build.query({
      query: () => "client/customers",
      providesTags: ["Customers"],
    }),
    syncShopifyCustomers: build.mutation({
      query: () => ({
        url: "shopify/import-customers",
        method: "GET",
      }),
      invalidatesTags: ["Customers"],
    }),
    getTransactions: build.query({
      query: ({ page, pageSize, sort, search }) => ({
        url: "client/transactions",
        method: "GET",
        params: { page, pageSize, sort, search },
      }),
      providesTags: ["Transactions"],
    }),
    getGeography: build.query({
      query: () => "client/geography",
      providesTags: ["Geography"],
    }),
    getSales: build.query({
      query: () => "sales/sales",
      providesTags: ["Sales"],
    }),
    getAdmins: build.query({
      query: () => "management/admins",
      providesTags: ["Admins"],
    }),
    getPerformanceSummary: build.query({
      query: () => "management/performance",
      providesTags: ["Performance"],
    }),
    getUserPerformance: build.query({
      query: (id) => `management/performance/${id}`,
      providesTags: ["Performance"],
    }),
    getDashboard: build.query({
      query: () => "general/dashboard",
      providesTags: ["Dashboard"],
    }),
    getOrders: build.query({
      query: () => "client/orders",
      providesTags: ["Orders"],
    }),
    syncShopifyOrders: build.mutation({
      query: () => ({
        url: "shopify/import-orders",
        method: "GET",
      }),
      invalidatesTags: ["Orders"],
    }),
    getAbandonedCarts: build.query({
      query: ({ status = "all", search = "", market = "all" } = {}) => ({
        url: "abandoned-carts",
        params: { status, search, market: market === "all" ? "" : market },
      }),
      providesTags: ["AbandonedCarts"],
    }),
    getAbandonedCartStats: build.query({
      query: () => "abandoned-carts/stats",
      providesTags: ["AbandonedCarts"],
    }),
    patchAbandonedCart: build.mutation({
      query: ({ id, status }) => ({
        url: `abandoned-carts/${id}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["AbandonedCarts"],
    }),
    getExaminedProducts: build.query({
      query: ({ market = "tr", search = "", sort = "views" } = {}) => ({
        url: "popular-products",
        params: { market, search, sort },
      }),
      providesTags: ["PopularProducts"],
    }),
    deleteExaminedProduct: build.mutation({
      query: (id) => ({
        url: `popular-products/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["PopularProducts"],
    }),
    getBlogPosts: build.query({
      query: ({ market = "tr", status = "", search = "" } = {}) => ({
        url: "blog-posts",
        params: { market, status, search },
      }),
      providesTags: ["BlogPosts"],
    }),
    getBlogPost: build.query({
      query: (id) => `blog-posts/${id}`,
      providesTags: (_r, _e, id) => [{ type: "BlogPosts", id }],
    }),
    createBlogPostFromProduct: build.mutation({
      query: (body) => ({
        url: "blog-posts/from-product",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BlogPosts"],
    }),
    updateBlogPost: build.mutation({
      query: ({ id, ...body }) => ({
        url: `blog-posts/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["BlogPosts"],
    }),
    publishBlogPost: build.mutation({
      query: (id) => ({
        url: `blog-posts/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: ["BlogPosts"],
    }),
    refreshBlogStockCover: build.mutation({
      query: (id) => ({
        url: `blog-posts/${id}/refresh-cover`,
        method: "POST",
      }),
      invalidatesTags: ["BlogPosts"],
    }),
    unpublishBlogPost: build.mutation({
      query: (id) => ({
        url: `blog-posts/${id}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: ["BlogPosts"],
    }),
    deleteBlogPost: build.mutation({
      query: (id) => ({
        url: `blog-posts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BlogPosts"],
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetProductsQuery,
  useGetCustomersQuery,
  useSyncShopifyCustomersMutation,
  useGetTransactionsQuery,
  useGetGeographyQuery,
  useGetSalesQuery,
  useGetAdminsQuery,
  useGetPerformanceSummaryQuery,
  useGetUserPerformanceQuery,
  useGetDashboardQuery,
  useGetOrdersQuery,
  useSyncShopifyOrdersMutation,
  useGetAbandonedCartsQuery,
  useGetAbandonedCartStatsQuery,
  usePatchAbandonedCartMutation,
  useGetExaminedProductsQuery,
  useDeleteExaminedProductMutation,
  useGetBlogPostsQuery,
  useCreateBlogPostFromProductMutation,
  useUpdateBlogPostMutation,
  usePublishBlogPostMutation,
  useRefreshBlogStockCoverMutation,
  useUnpublishBlogPostMutation,
  useDeleteBlogPostMutation,
} = api;
